import { FC } from "react";

import { chakra } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";

import Icon from "@components/Icon";
import { useRouter } from "@lib/router";
import { ROUTES } from "@utils/constants";
import joinDefinedStringParts from "@utils/joinDefinedStringParts";

import { DeliveryMethod, OrderFields } from "../../../constants";
import useOrderFormik from "../../../hooks/useOrderFormik";
import getAddressString from "../../../utils/getAddressString";
import OrderInfoItem from "../OrderInfoItem";

const OrderInfoSender: FC = () => {
  const { t } = useTranslation();

  const {
    values: {
      [OrderFields.DELIVERY_METHOD]: deliveryMethod,
      [OrderFields.SENDER_NAME]: senderName,
      [OrderFields.SENDER_EMAIL]: senderEmail,
      [OrderFields.SENDER_PHONE_NUMBER]: senderPhone,
      [OrderFields.RETURN_LOCKER_NAME]: senderLockerName,
      [OrderFields.SENDER_STREET]: senderStreet,
      [OrderFields.SENDER_HOUSE_NO]: senderHouseNo,
      [OrderFields.SENDER_FLAT_NO]: senderFlatNo,
      [OrderFields.SENDER_CITY]: senderCity,
      [OrderFields.SENDER_ZIP_CODE]: senderZipCode,
      [OrderFields.STARTING_COUNTRY_NAME]: senderCountry,
      [OrderFields.SENDER_COMMENTS]: senderComments,
      [OrderFields.PICKUP_DATE]: pickupDate,
      [OrderFields.PICKUP_TIME_FROM]: pickupTimeFrom,
      [OrderFields.PICKUP_TIME_TILL]: pickupTimeTo,
    },
  } = useOrderFormik();

  const router = useRouter();

  const senderAddressParams = {
    street: senderStreet || undefined,
    houseNo: senderHouseNo || undefined,
    flatNo: senderFlatNo || undefined,
    city: senderCity || undefined,
    zip: senderZipCode || undefined,
    country: senderCountry || undefined,
    comments: senderComments || undefined,
  };

  const senderAddress =
    deliveryMethod === DeliveryMethod.PudoToDoor || deliveryMethod === DeliveryMethod.PudoToPudo
      ? `${senderLockerName}, ${senderCountry}`
      : getAddressString(senderAddressParams);

  const senderAddressLocker =
    deliveryMethod === DeliveryMethod.DoorToPudo || deliveryMethod === DeliveryMethod.PudoToPudo
      ? getAddressString(senderAddressParams)
      : "";

  const pickupTimes = joinDefinedStringParts([pickupTimeFrom, pickupTimeTo], " - ");
  const pickupDescription = joinDefinedStringParts([pickupDate, pickupTimes], ", ");

  const senderDescription = (
    <>
      <chakra.p wordBreak="break-word">
        {`${joinDefinedStringParts([senderName, senderPhone], ", ")}, `}
        <chakra.span>{senderEmail}</chakra.span>
      </chakra.p>
      <chakra.p>{senderAddress}</chakra.p>
      <chakra.p fontSize="sm" color="gray.500">
        {senderAddressLocker}
      </chakra.p>
      <chakra.p fontWeight="600">{pickupDescription}</chakra.p>
    </>
  );

  const handleEditClick = () => {
    if (deliveryMethod === DeliveryMethod.PudoToPudo || deliveryMethod === DeliveryMethod.PudoToDoor) {
      router.push(ROUTES.ORDER_SHIPMENT);
      return;
    }
    router.push(ROUTES.ORDER_SENDER);
  };

  return (
    <OrderInfoItem
      id="order-sender"
      title={t("order-summary-sender")}
      description={senderDescription}
      isFixedWidthTitle
      image={<Icon icon="profile" size="1.75rem" />}
      onEditClick={handleEditClick}
    />
  );
};
export default OrderInfoSender;
